import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCourseApi, CourseDetails, Unit, Lesson } from '../../hooks/useCourseApi';
import { Curriculum } from './Curriculum';
import { checkEnrollment } from '../../services/enrollmentService';
import { BASE_URL } from '../../apiConfig';
import axios from 'axios';
import { getTenantHeaders } from '../../config/tenant';
import UnitEnrollmentPopup from './UnitEnrollmentPopup';
import { 
  BookOpen, 
  Clock, 
  Users, 
  Star, 
  Play, 
  ArrowRight, 
  CheckCircle, 
  Award,
  GraduationCap,
  Calendar,
  Video,
  Target,
  Zap,
  MessageCircle,
  Phone,
  Mail,
  ExternalLink
} from 'lucide-react';

// Grade translator function
const getGradeInArabic = (grade: string): string => {
  const gradeTranslations: Record<string, string> = {
    'Secondary1': 'أولى ثانوي',
    'Secondary2': 'ثانية ثانوي',
    'Secondary3': 'ثالثة ثانوي',
    'default': grade
  };
  return gradeTranslations[grade] || grade;
};

// Term translator function
const getTermInArabic = (term: string): string => {
  const terms: Record<string, string> = {
    'First': 'الفصل الأول',
    'Second': 'الفصل الثاني',
    'Summer': 'الفصل الصيفي'
  };
  return terms[term] || term;
};

interface ContactInfo {
  id: number;
  whatsApp_Number: string;
  facebook_Page: string;
  youTube_Channel: string;
  tiktokChannel: string;
}

interface ContactApiResponse {
  success: boolean;
  message: string;
  data: ContactInfo[];
}

const CourseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated, currentUser } = useAuth();
  const navigate = useNavigate();
  const { fetchCourseDetailsWithoutProgress, isLoading, error, enrollInCourse } = useCourseApi();

  // Helper function to construct course image URL
  const getCourseImageSrc = (courseDetails: CourseDetails) => {
    if (!courseDetails.imagePath) {
      return '/default-course.jpg';
    }
    
    if (courseDetails.imagePath.startsWith('http')) {
      return courseDetails.imagePath;
    }
    
    let imagePath = courseDetails.imagePath;
    if (imagePath.startsWith('/')) {
      imagePath = imagePath.substring(1);
    }
    
    const baseUrl = BASE_URL.endsWith('/') ? BASE_URL : BASE_URL + '/';
    return `${baseUrl}${imagePath}`;
  };

  const [selectedSection, setSelectedSection] = useState('curriculum');
  const [courseDetails, setCourseDetails] = useState<CourseDetails | null>(null);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [isCheckingEnrollment, setIsCheckingEnrollment] = useState(true);
  
  // Enrollment states
  const [showEnrollmentOptions, setShowEnrollmentOptions] = useState(true);
  const [showCodeInput, setShowCodeInput] = useState(true);
  const [code, setCode] = useState('');
  const [enrollmentError, setEnrollmentError] = useState('');
  const [enrollmentSuccess, setEnrollmentSuccess] = useState(false);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  
  // Unit enrollment states
  const [showUnitEnrollmentPopup, setShowUnitEnrollmentPopup] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [unitEnrollmentStatus, setUnitEnrollmentStatus] = useState<Map<number, boolean>>(new Map());

  // Set unit enrollment status based on course enrollment status
  const setUnitEnrollmentFromCourseStatus = useCallback(() => {
    if (!courseDetails) return;
    
    // If user is enrolled in the course, all units are considered enrolled
    const enrollmentMap = new Map<number, boolean>();
    courseDetails.units.forEach(unit => {
      enrollmentMap.set(unit.id, isEnrolled);
    });
    
    setUnitEnrollmentStatus(enrollmentMap);
  }, [courseDetails, isEnrolled]);
  
  useEffect(() => {
    const loadCourseDetails = async () => {
      if (id) {
        const response = await fetchCourseDetailsWithoutProgress(id);
        if (response?.success) {
          setCourseDetails(response.data);
        }
      }
    };

    loadCourseDetails();
  }, [id, fetchCourseDetailsWithoutProgress]);

  useEffect(() => {
    const checkUserEnrollment = async () => {
      if (id && currentUser?.id) {
        setIsCheckingEnrollment(true);
        const enrolled = await checkEnrollment(currentUser.id, id);
        setIsEnrolled(enrolled);
        setIsCheckingEnrollment(false);
      } else {
        setIsCheckingEnrollment(false);
      }
    };

    checkUserEnrollment();
  }, [id, currentUser?.id]);

  // Set unit enrollment status when course details and enrollment status are available
  useEffect(() => {
    if (courseDetails && !isCheckingEnrollment) {
      setUnitEnrollmentFromCourseStatus();
    }
  }, [courseDetails, isEnrolled, isCheckingEnrollment, setUnitEnrollmentFromCourseStatus]);

  useEffect(() => {
    const fetchWhatsAppNumber = async () => {
      try {
        const response = await axios.get<ContactApiResponse>('https://api.ibrahim-magdy.com/api/Contact/getAll', {
          headers: {
            ...getTenantHeaders()
          }
        });
        if (response.data.success && response.data.data.length > 0) {
          setWhatsappNumber(response.data.data[0].whatsApp_Number);
        }
      } catch (error) {
        console.error('Error fetching WhatsApp number:', error);
      }
    };

    fetchWhatsAppNumber();
  }, []);

  const handleEnroll = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { redirectTo: `/courses/${id}` } });
      return;
    }
    setShowEnrollmentOptions(true);
  };

  const handleContinue = () => {
    navigate(`/course-player/${id}`);
  };

  const handleWhatsAppContact = () => {
    if (whatsappNumber) {
      const whatsappUrl = `https://wa.me/${whatsappNumber}?text=مرحباً، أريد الاستفسار عن التسجيل في الدورة: ${courseDetails?.courseName}`;
      window.open(whatsappUrl, '_blank');
    }
  };

  const handleCodeSubmit = async () => {
    if (!code.trim()) {
      setEnrollmentError('يرجى إدخال كود التسجيل');
      setEnrollmentSuccess(false);
      return;
    }

    if (!isAuthenticated) {
      setEnrollmentError('يرجى تسجيل الدخول للتسجيل في هذه الدورة');
      setEnrollmentSuccess(false);
      return;
    }

    try {
      setIsEnrolling(true);
      setEnrollmentError('');
      setEnrollmentSuccess(false);
      const response = await enrollInCourse(id || '', code);
      
      if (response.success && response.message !== 'Code not found.') {
        setEnrollmentSuccess(true);
        setEnrollmentError('');
        setShowSuccessPopup(true);
        if (id && currentUser?.id) {
          const enrolled = await checkEnrollment(currentUser.id, id);
          setIsEnrolled(enrolled);
          // Update unit enrollment status after successful course enrollment
          if (enrolled && courseDetails) {
            const enrollmentMap = new Map<number, boolean>();
            courseDetails.units.forEach(unit => {
              enrollmentMap.set(unit.id, true);
            });
            setUnitEnrollmentStatus(enrollmentMap);
          }
        }
      } else {
        setEnrollmentSuccess(false);
        setEnrollmentError(response.message || 'كود التسجيل غير صحيح');
      }
    } catch (err) {
      setEnrollmentSuccess(false);
      setEnrollmentError('حدث خطأ أثناء التسجيل في الدورة');
    } finally {
      setIsEnrolling(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isEnrolling) {
      handleCodeSubmit();
    }
  };

  const resetEnrollmentStates = () => {
    setShowEnrollmentOptions(false);
    setShowCodeInput(false);
    setCode('');
    setEnrollmentError('');
    setEnrollmentSuccess(false);
    setShowSuccessPopup(false);
  };

  const handleStartCourse = () => {
    setShowSuccessPopup(false);
    navigate(`/course-player/${id}`);
  };

  const handleGoToMyCourses = () => {
    setShowSuccessPopup(false);
    navigate('/my-lectures');
  };

  // Get unit enrollment status from cached state
  const getUnitEnrollmentStatus = (unitId: number): boolean => {
    return unitEnrollmentStatus.get(unitId) || false;
  };

  // Handle unit click
  const handleUnitClick = (unit: Unit) => {
    if (!currentUser?.id) {
      navigate('/login', { state: { redirectTo: `/courses/${id}` } });
      return;
    }

    // Check if user is enrolled in this unit from cached state
    const isEnrolled = getUnitEnrollmentStatus(unit.id);
    
    if (isEnrolled) {
      // If enrolled, navigate to unit viewer or course player
      navigate(`/course-player/${id}`);
    } else {
      // If not enrolled, show enrollment popup
      setSelectedUnit(unit);
      setShowUnitEnrollmentPopup(true);
    }
  };

  // Handle unit enrollment popup close
  const handleUnitEnrollmentClose = () => {
    setShowUnitEnrollmentPopup(false);
    setSelectedUnit(null);
  };

  // Handle lesson click - same logic as unit click
  const handleLessonSelect = (lesson: Lesson, unit: Unit) => {
    if (!currentUser?.id) {
      navigate('/login', { state: { redirectTo: `/courses/${id}` } });
      return;
    }

    // Check if user is enrolled in this unit from cached state
    const isEnrolled = getUnitEnrollmentStatus(unit.id);
    
    if (isEnrolled) {
      // If enrolled, navigate to course player
      navigate(`/course-player/${id}`);
    } else {
      // If not enrolled, show enrollment popup with unit information
      setSelectedUnit(unit);
      setShowUnitEnrollmentPopup(true);
    }
  };

  const tabs = [
    { id: 'curriculum', label: 'المنهج الدراسي', icon: BookOpen },
    { id: 'overview', label: 'نظرة عامة', icon: Target },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
            <div className="w-16 h-16 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto absolute inset-0 animate-pulse" style={{ animationDelay: '0.5s' }}></div>
          </div>
          <p className="text-gray-600 text-lg font-medium">جاري تحميل تفاصيل الدورة...</p>
        </div>
      </div>
    );
  }

  if (error || !courseDetails) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 flex items-center justify-center">

      </div>
    );
  }
  
  const totalLessons = courseDetails.units.reduce((total, unit) => total + unit.lessons.length, 0);

  return (
    <div className="min-h-screen bg-white font-cairo" dir="rtl">
      {/* Success Popup Modal */}
      {showSuccessPopup && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 transform animate-in slide-in-from-bottom-4 duration-300">
            <div className="text-center mb-6">
              <div className="mx-auto w-16 h-16 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">تم التسجيل بنجاح! 🎉</h3>
              <p className="text-gray-600 text-sm">مرحباً بك في {courseDetails?.courseName}</p>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleStartCourse}
                className="w-full px-4 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg font-semibold text-base hover:from-green-700 hover:to-green-800 transform hover:-translate-y-1 transition-all duration-300 shadow-lg shadow-green-500/25 flex items-center justify-center gap-2"
              >
                <Play className="w-4 h-4" />
                بدأ الدورة الآن
              </button>
              <button
                onClick={handleGoToMyCourses}
                className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-semibold text-base hover:from-blue-700 hover:to-blue-800 transform hover:-translate-y-1 transition-all duration-300 shadow-lg shadow-blue-500/25 flex items-center justify-center gap-2"
              >
                <BookOpen className="w-4 h-4" />
                الانتقال الى باقاتي
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 text-gray-900 overflow-hidden pt-4 sm:pt-6">
        {/* Background Pattern */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full opacity-20 sm:opacity-30">
            <div className="absolute top-10 left-10 w-48 h-48 sm:w-72 sm:h-72 bg-blue-200 rounded-full blur-2xl sm:blur-3xl animate-pulse"></div>
            <div className="absolute bottom-10 right-10 w-64 h-64 sm:w-96 sm:h-96 bg-purple-200 rounded-full blur-2xl sm:blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-56 h-56 sm:w-80 sm:h-80 bg-indigo-200 rounded-full blur-2xl sm:blur-3xl animate-pulse" style={{ animationDelay: '4s' }}></div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16 relative z-10">
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center">
            {/* Course Image */}
            <div className="order-1">
              <div className="relative mx-auto max-w-xs sm:max-w-sm">
                <div className="absolute -inset-3 sm:-inset-4 bg-gradient-to-r from-blue-100 to-purple-100 rounded-2xl sm:rounded-3xl blur-xl sm:blur-2xl"></div>
                <div className="relative rounded-xl sm:rounded-2xl overflow-hidden shadow-lg sm:shadow-xl border border-white/50 transform hover:scale-[1.02] transition-all duration-500">
                  <img 
                    src={getCourseImageSrc(courseDetails)} 
                    alt={courseDetails.courseName}
                    className="w-full h-auto object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = '/default-course.jpg';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 via-gray-900/20 to-transparent"></div>
                  
                  {/* Course Stats Overlay */}
                  <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 right-3 sm:right-4">
                    <div className="flex items-center gap-2 sm:gap-3 text-white">
                      <div className="flex items-center gap-1 sm:gap-2 bg-white/20 backdrop-blur-sm rounded-full px-2 py-1 sm:px-3 sm:py-1.5">
                        <Video className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="text-xs sm:text-sm font-medium">{totalLessons} درس</span>
                      </div>
                      
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Course Info */}
            <div className="order-2 text-center lg:text-right">
              {/* Course Badge */}
              <div className="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 bg-white/80 backdrop-blur-sm rounded-full mb-4 sm:mb-6 gap-2 sm:gap-3 border border-gray-200/50 shadow-sm">
                <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-400 rounded-full animate-pulse"></div>
                <span className="text-blue-700 text-xs sm:text-sm font-medium">{getGradeInArabic(courseDetails.grade)}</span>
                <div className="w-0.5 h-0.5 sm:w-1 sm:h-1 bg-gray-400 rounded-full"></div>
                <span className="text-purple-700 text-xs sm:text-sm font-medium">{getTermInArabic(courseDetails.term)}</span>
              </div>

              {/* Course Title */}
              <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold mb-4 sm:mb-6 leading-tight text-gray-900">
                {courseDetails.courseName}
              </h1>

              {/* Course Description */}
              <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                {courseDetails.description}
              </p>
              
              {/* Course Features */}
              <div className="flex flex-wrap gap-2 sm:gap-3 mb-6 sm:mb-8 justify-center lg:justify-start">
                <div className="bg-white/60 backdrop-blur-sm rounded-lg p-2 sm:p-3 border border-gray-200/50 shadow-sm">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-100 rounded-lg flex items-center justify-center">
                      <GraduationCap className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">المستوى</div>
                      <div className="text-xs font-semibold text-gray-900">{getGradeInArabic(courseDetails.grade)}</div>
                    </div>
                  </div>
                </div>
                <div className="bg-white/60 backdrop-blur-sm rounded-lg p-2 sm:p-3 border border-gray-200/50 shadow-sm">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Calendar className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-purple-600" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">الفصل</div>
                      <div className="text-xs font-semibold text-gray-900">{getTermInArabic(courseDetails.term)}</div>
                    </div>
                  </div>
                </div>
                <div className="bg-white/60 backdrop-blur-sm rounded-lg p-2 sm:p-3 border border-gray-200/50 shadow-sm">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 bg-green-100 rounded-lg flex items-center justify-center">
                      <Award className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-green-600" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">السعر</div>
                      <div className="text-xs font-semibold text-gray-900">
                        {courseDetails.price === 0 ? 'مجاناً' : `${courseDetails.price.toFixed(2)} جنيه`}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Enrollment Section */}
              <div className="space-y-3 sm:space-y-4">
                {isCheckingEnrollment ? (
                  <div className="flex items-center justify-center gap-2 sm:gap-3">
                    <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-2 border-gray-400 border-t-transparent"></div>
                    <span className="text-gray-600 text-xs sm:text-sm">جاري التحقق من التسجيل...</span>
                  </div>
                ) : isEnrolled ? (
                  <button
                    onClick={handleContinue}
                    className="w-full lg:w-auto px-4 py-2.5 sm:px-6 sm:py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base hover:from-green-700 hover:to-emerald-700 transform hover:-translate-y-1 transition-all duration-300 shadow-lg shadow-green-500/25 flex items-center justify-center gap-2"
                  >
                    <Play className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    استمرار الدورة
                  </button>
                ) : (
                  <div className="space-y-3 sm:space-y-4">
                    {courseDetails.price === 0 ? (
                      <button
                        onClick={() => navigate(`/course-player/${id}`)}
                        className="w-full lg:w-auto px-4 py-2.5 sm:px-6 sm:py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base hover:from-green-700 hover:to-emerald-700 transform hover:-translate-y-1 transition-all duration-300 shadow-lg shadow-green-500/25 flex items-center justify-center gap-2"
                      >
                        <Play className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        بدأ الدورة المجانية
                      </button>
                    ) : (
                      <div className="space-y-3 sm:space-y-4">
                        <button
                          onClick={handleWhatsAppContact}
                          className="w-full lg:w-auto px-3 py-2 sm:px-4 sm:py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-medium text-xs sm:text-sm hover:from-green-700 hover:to-emerald-700 transform hover:-translate-y-1 transition-all duration-300 shadow-lg shadow-green-500/25 flex items-center justify-center gap-2"
                        >
                          <MessageCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                          التواصل مع الإشراف
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Code Input Section */}
              {showCodeInput && courseDetails.price > 0 && (
                <div className="mt-6 sm:mt-8 p-4 sm:p-6 bg-white/60 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-gray-200/50 shadow-sm">
                  <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-3 sm:mb-4 text-center">لديك كود تسجيل؟</h3>
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 items-center">
                    <input
                      type="text"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="أدخل كود التسجيل"
                      className={`flex-1 px-3 py-2 sm:px-4 sm:py-2 border-2 rounded-lg sm:rounded-xl focus:outline-none transition-all duration-200 text-gray-900 placeholder-gray-500 text-xs sm:text-sm
                        ${enrollmentError ? 'border-red-300 bg-red-50' : 'border-gray-200 focus:border-blue-500 focus:bg-white'}
                      `}
                      disabled={isEnrolling}
                    />
                    <button
                      onClick={handleCodeSubmit}
                      disabled={isEnrolling}
                      className="w-full sm:w-auto px-3 py-2 sm:px-4 sm:py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg sm:rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 disabled:opacity-50 font-semibold text-xs sm:text-sm flex items-center justify-center gap-2"
                    >
                      {isEnrolling ? (
                        <>
                          <div className="animate-spin rounded-full h-2.5 w-2.5 sm:h-3 sm:w-3 border-2 border-white border-t-transparent"></div>
                          جاري التسجيل...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                          تسجيل
                        </>
                      )}
                    </button>
                  </div>
                  {enrollmentError && (
                    <div className="mt-2 sm:mt-3 text-red-600 text-xs text-center bg-red-100 rounded-lg py-1.5 px-2 sm:py-2 sm:px-3">
                      {enrollmentError}
                    </div>
                  )}
                  {enrollmentSuccess && (
                    <div className="mt-2 sm:mt-3 text-green-600 text-xs text-center bg-green-100 rounded-lg py-1.5 px-2 sm:py-2 sm:px-3">
                      تم التسجيل بنجاح! 🎉
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="py-8 sm:py-12 lg:py-16 bg-white relative">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Tabs */}
          <div className="flex justify-center mb-6 sm:mb-8 lg:mb-10">
            <div className="bg-gray-100 rounded-lg sm:rounded-xl p-1 flex gap-1">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setSelectedSection(tab.id)}
                  className={`flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-5 sm:py-2 rounded-md sm:rounded-lg font-medium transition-all duration-300 text-xs sm:text-sm ${
                    selectedSection === tab.id
                      ? 'bg-white text-blue-600 shadow-sm transform -translate-y-0.5'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-200'
                  }`}
                >
                  <tab.icon className="w-3 h-3 sm:w-4 sm:h-4" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl sm:rounded-2xl shadow-lg p-6 sm:p-8 lg:p-10 border border-gray-100">
            {selectedSection === 'curriculum' ? (
              <div>
                <div className="text-center mb-6 sm:mb-8 lg:mb-10">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">المنهج الدراسي</h2>
                  <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
                    اكتشف محتوى الدورة الكامل مع {totalLessons} درس منظم ومنسق بعناية
                  </p>
                </div>
                <Curriculum 
                  units={courseDetails.units} 
                  courseId={courseDetails.id} 
                  onUnitClick={handleUnitClick}
                  onLessonSelect={handleLessonSelect}
                />
              </div>
            ) : (
              <div className="max-w-4xl mx-auto">
                <div className="text-center mb-6 sm:mb-8 lg:mb-10">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">نظرة عامة على الدورة</h2>
                  <p className="text-sm sm:text-base text-gray-600">
                    تعرف على تفاصيل الدورة وأهدافها التعليمية
                  </p>
                </div>

                <div className="prose prose-lg max-w-none">
                  <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg sm:rounded-xl p-4 sm:p-6 mb-4 sm:mb-6">
                    <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 text-gray-900 flex items-center gap-2 sm:gap-3">
                      <Target className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                      وصف الدورة
                    </h3>
                    <p className="text-sm sm:text-base text-gray-700 leading-relaxed">{courseDetails.description}</p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
                    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-4 sm:p-6 rounded-lg sm:rounded-xl shadow-sm border border-emerald-100 hover:shadow-md hover:-translate-y-1 transition-all duration-300">
                      <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-gray-900 flex items-center gap-2 sm:gap-3">
                        <GraduationCap className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
                        تفاصيل الدورة
                      </h3>
                      <ul className="space-y-2 sm:space-y-3">
                        <li className="flex items-center gap-2 sm:gap-3">
                          <div className="w-5 h-5 sm:w-6 sm:h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <CheckCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-emerald-600" />
                          </div>
                          <span className="text-gray-700 text-xs sm:text-sm">الصف: {getGradeInArabic(courseDetails.grade)}</span>
                        </li>
                        <li className="flex items-center gap-2 sm:gap-3">
                          <div className="w-5 h-5 sm:w-6 sm:h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <CheckCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-purple-600" />
                          </div>
                          <span className="text-gray-700 text-xs sm:text-sm">الفصل الدراسي: {getTermInArabic(courseDetails.term)}</span>
                        </li>
                        <li className="flex items-center gap-2 sm:gap-3">
                          <div className="w-5 h-5 sm:w-6 sm:h-6 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <CheckCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-pink-600" />
                          </div>
                          <span className="text-gray-700 text-xs sm:text-sm">عدد الدروس: {totalLessons} درس</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-4 sm:p-6 rounded-lg sm:rounded-xl shadow-sm border border-purple-100 hover:shadow-md hover:-translate-y-1 transition-all duration-300">
                      <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-gray-900 flex items-center gap-2 sm:gap-3">
                        <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                        مميزات الدورة
                      </h3>
                      <ul className="space-y-2 sm:space-y-3">
                        <li className="flex items-center gap-2 sm:gap-3">
                          <div className="w-5 h-5 sm:w-6 sm:h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <CheckCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-purple-600" />
                          </div>
                          <span className="text-gray-700 text-xs sm:text-sm">شرح تفصيلي ومفصل</span>
                        </li>
                        <li className="flex items-center gap-2 sm:gap-3">
                          <div className="w-5 h-5 sm:w-6 sm:h-6 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <CheckCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-indigo-600" />
                          </div>
                          <span className="text-gray-700 text-xs sm:text-sm">تمارين وتطبيقات عملية</span>
                        </li>
                        <li className="flex items-center gap-2 sm:gap-3">
                          <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <CheckCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-blue-600" />
                          </div>
                          <span className="text-gray-700 text-xs sm:text-sm">دعم متواصل من المدرس</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div className="py-8 sm:py-12 lg:py-16 bg-gradient-to-br from-gray-50 to-blue-50/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-gray-900">هل لديك أسئلة حول الدورة؟</h2>
          <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
            نحن هنا لمساعدتك! تواصل معنا للحصول على الدعم والإجابة على استفساراتك
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <button
              onClick={handleWhatsAppContact}
              className="px-4 py-2.5 sm:px-6 sm:py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg sm:rounded-xl font-semibold hover:from-green-700 hover:to-emerald-700 transform hover:-translate-y-1 transition-all duration-300 shadow-lg shadow-green-500/25 flex items-center justify-center gap-2 text-xs sm:text-sm"
            >
              <MessageCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              التواصل عبر الواتساب
            </button>
            
            <button className="px-4 py-2.5 sm:px-6 sm:py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg sm:rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transform hover:-translate-y-1 transition-all duration-300 shadow-lg shadow-blue-500/25 flex items-center justify-center gap-2 text-xs sm:text-sm">
              <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              إرسال رسالة
            </button>
          </div>
        </div>
      </div>

      {/* Unit Enrollment Popup */}
      {selectedUnit && (
        <UnitEnrollmentPopup
          isOpen={showUnitEnrollmentPopup}
          onClose={handleUnitEnrollmentClose}
          unitId={selectedUnit.id}
          unitName={selectedUnit.unitName}
        />
      )}
    </div>
  );
};

export default CourseDetail; 
